import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchMyOrgs, switchOrg, type MyOrg } from "@/api/orgs";

interface OrgContextValue {
  orgs: MyOrg[];
  currentOrgId: string | null;
  currentOrg: MyOrg | null;
  /** Switch active org via ait-auth, then refresh all org-scoped reads. */
  setCurrentOrg: (id: string) => void;
  ready: boolean;
}

const Ctx = createContext<OrgContextValue | undefined>(undefined);

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();

  // Membership + active org come from ait-auth (proxied). The active org is
  // authoritative from the token; localStorage is no longer used.
  const { data, isSuccess } = useQuery({
    queryKey: ["my-orgs"],
    queryFn: fetchMyOrgs,
    staleTime: 60_000,
  });

  const orgs = data?.organizations ?? [];
  const activeId = data?.active_org_id ?? null;
  // The backend blanks active_org_id when the token points at an org the user
  // can't use in kraken. Fall back to the first usable org for display.
  const currentOrgId =
    activeId && orgs.some((o) => o.id === activeId) ? activeId : orgs[0]?.id ?? null;

  // Reconcile the session when the token's active org isn't one we can use:
  // rotate it server-side to a valid org so subsequent requests carry a
  // correct `org` claim (not just a corrected display).
  useEffect(() => {
    if (!isSuccess || orgs.length === 0) return;
    if (activeId && orgs.some((o) => o.id === activeId)) return;
    if (!currentOrgId) return;
    switchOrg(currentOrgId)
      .then(() => qc.invalidateQueries())
      .catch(() => {
        /* leave as-is; reads will 409 and surface an org-switch prompt */
      });
  }, [isSuccess, activeId, currentOrgId, orgs, qc]);

  const currentOrg = useMemo(
    () => orgs.find((o) => o.id === currentOrgId) ?? null,
    [orgs, currentOrgId],
  );

  const setCurrentOrg = useCallback(
    (id: string) => {
      if (id === currentOrgId) return;
      // Rotate the kraken session at ait-auth, then drop every cached read so
      // they refetch under the new org claim.
      switchOrg(id)
        .then(() => qc.invalidateQueries())
        .catch(() => {
          /* surfaced by callers / toast; leave current org in place */
        });
    },
    [qc, currentOrgId],
  );

  const value: OrgContextValue = {
    orgs,
    currentOrgId,
    currentOrg,
    setCurrentOrg,
    ready: isSuccess,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOrg(): OrgContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useOrg called outside OrgProvider");
  return v;
}
